import { useFormikContext } from 'formik';
import { FC, useCallback, useMemo } from 'react';

import { RangeFilter } from '@controls/RangeFilter';

import { fromKopecksToRouble } from '@scripts/helpers';

import { filterTitleStyles } from '../../../../styles';
import { FormValues } from '../../../../types';
import {
    calculatePriceRangeBounds,
    getFieldName,
    handlePriceAfterChange,
    isNumberArray,
    isPriceRangeDefault,
} from '../../../../utils';
import { containerStyles } from './styles';
import { PriceFilterSectionProps } from './types';

const PriceFilterSection: FC<PriceFilterSectionProps> = ({
    hidePriceFilter,
    fieldPrefix,
    currentPriceRange,
    priceRange,
    rangeDefault,
    rememberedPrice,
    priceFromUrl,
    setRememberedPrice,
    onFilterChange,
}) => {
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const priceFieldName = getFieldName('price', fieldPrefix);

    const currentRangeDefault = useMemo(() => {
        return currentPriceRange ? [currentPriceRange.min, currentPriceRange.max] : rangeDefault;
    }, [currentPriceRange, rangeDefault]);

    const handlePriceChange = useCallback(
        (newValue: number[]) => {
            const [min, max] = newValue;

            if (isPriceRangeDefault(min, max, currentPriceRange)) {
                setRememberedPrice(undefined);
                setFieldValue(priceFieldName, undefined);
                onFilterChange?.('price', []);
            } else {
                setRememberedPrice(newValue);
                setFieldValue(priceFieldName, newValue);
                onFilterChange?.('price', newValue);
            }
        },
        [currentPriceRange, setFieldValue, priceFieldName, onFilterChange, setRememberedPrice]
    );

    const priceStep = useMemo(() => {
        return currentPriceRange ? fromKopecksToRouble(currentPriceRange.max - currentPriceRange.min) / 10 : 10;
    }, [currentPriceRange]);

    if (hidePriceFilter) {
        return null;
    }

    return (
        <div css={containerStyles}>
            <div css={filterTitleStyles}>Цена (₽)</div>
            <RangeFilter
                {...calculatePriceRangeBounds(rememberedPrice, currentPriceRange)}
                step={priceStep}
                value={isNumberArray(values[priceFieldName]) ? values[priceFieldName] : currentRangeDefault}
                code="price"
                onChange={handlePriceChange}
                formatThousands
                onAfterChange={newValue => {
                    handlePriceAfterChange({
                        newValue,
                        values,
                        fieldPrefix,
                        priceFromUrl,
                        currentRangeDefault,
                        priceRange,
                        setRememberedPrice,
                        setFieldValue,
                        handlePriceChange,
                    });
                }}
            />
        </div>
    );
};

export default PriceFilterSection;
